import { isUserParticipating } from './auction';
import { parseCanSeePermission, parseExplicitCanSeePermission } from './canSeePermission';

export { parseCanSeePermission, parseExplicitCanSeePermission } from './canSeePermission';

export const PRICE_VISIBILITY_PAGES = {
  HOME: 'home',
  DETAILS: 'details',
};

function collectPermissionFieldValues(entity, camelKey, snakeKey) {
  if (!entity) {
    return [];
  }

  return [
    entity[camelKey],
    entity[snakeKey],
    entity.profile?.[camelKey],
    entity.profile?.[snakeKey],
  ];
}

export function getCanSeePermissionFromUser(user, dealerProfile, camelKey, snakeKey) {
  const dealerValues = collectPermissionFieldValues(dealerProfile, camelKey, snakeKey);
  const hasDealerPermissionField = dealerValues.some(
    value => value !== undefined && value !== null
  );

  if (hasDealerPermissionField) {
    return parseCanSeePermission(...dealerValues);
  }

  return parseCanSeePermission(...collectPermissionFieldValues(user, camelKey, snakeKey));
}

export function resolvePriceVisibilityPage({ page, section, pathname } = {}) {
  if (page === PRICE_VISIBILITY_PAGES.HOME || page === PRICE_VISIBILITY_PAGES.DETAILS) {
    return page;
  }

  if (section === 'details') {
    return PRICE_VISIBILITY_PAGES.DETAILS;
  }

  if (pathname?.startsWith('/detalhes')) {
    return PRICE_VISIBILITY_PAGES.DETAILS;
  }

  return PRICE_VISIBILITY_PAGES.HOME;
}

export function hasUserBidInAuction(auction, user, relatedDealers = []) {
  if (!auction?.auction || !user) {
    return false;
  }

  if (auction.auction.participating === true) {
    return true;
  }

  if (isUserParticipating(auction, user, relatedDealers)) {
    return true;
  }

  const bids = auction.auction.bids;
  if (!Array.isArray(bids) || !bids.length) {
    return false;
  }

  return bids.some(bid => {
    if (bid.bidder?.email === user.email) {
      return true;
    }

    if (bid.dealer?.email === user.email) {
      return true;
    }

    if (bid.dealer?.id && String(bid.dealer.id) === String(user.id ?? user._id)) {
      return true;
    }

    if (user.dealershipName && bid.dealership === user.dealershipName) {
      return true;
    }

    if (
      user.isNinja?.() &&
      relatedDealers.some(dealer => dealer.dealershipName === bid.dealership)
    ) {
      return true;
    }

    return false;
  });
}

function isPriceVisible(canSeePermission, hasBidInAuction) {
  if (canSeePermission) {
    return true;
  }

  return hasBidInAuction;
}

export function getPriceVisibility(
  user,
  auction,
  relatedDealers = [],
  page = PRICE_VISIBILITY_PAGES.HOME,
  featureFlags = {},
  dealerProfile = null
) {
  const hasBid = hasUserBidInAuction(auction, user, relatedDealers);
  const canSeeFipe = getCanSeePermissionFromUser(
    user,
    dealerProfile,
    'canSeeFipePrice',
    'can_see_fipe_price'
  );
  const canSeeExclusivity = getCanSeePermissionFromUser(
    user,
    dealerProfile,
    'canSeeNegotiateExclusively',
    'can_see_negotiate_exclusively'
  );
  const canSeeBuyFor = getCanSeePermissionFromUser(
    user,
    dealerProfile,
    'canSeeGuaranteedPurchase',
    'can_see_guaranteed_purchase'
  );

  const {
    canSeeNegotiateExclusivelyDetailsPage = false,
    canSeeGuaranteedPurchaseDetailsPage = false,
  } = featureFlags;

  if (page === PRICE_VISIBILITY_PAGES.DETAILS) {
    return {
      showFipe: true,
      showExclusivity: canSeeNegotiateExclusivelyDetailsPage
        ? isPriceVisible(canSeeExclusivity, hasBid)
        : true,
      showBuyFor: canSeeGuaranteedPurchaseDetailsPage
        ? isPriceVisible(canSeeBuyFor, hasBid)
        : true,
      hasBid,
    };
  }

  return {
    showFipe: isPriceVisible(canSeeFipe, hasBid),
    showExclusivity: isPriceVisible(canSeeExclusivity, hasBid),
    showBuyFor: isPriceVisible(canSeeBuyFor, hasBid),
    hasBid,
  };
}
